import {
  Button,
  Col,
  DatePicker,
  Divider,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Layout,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { TablePaginationConfig } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ICampaignsService } from "@shared/interfaces/campaigns-service.interface";
import { ROUTES } from "@routes/routes";
import {
  CampaignCpaVersionDto,
  CampaignDto,
  CreateCampaignDto,
} from "@shared/types/campaigns/campaign.dto";
import { CampaignStatus } from "@shared/types/campaigns/enums/campaign-status.enum";
import { CampaignType } from "@shared/types/campaigns/enums/campaign-type.enum";
import { formatTableDateTime } from "@shared/utils/format-datetime";
import styles from "./Campaigns.module.scss";

const getCampaignActionItems = (record: CampaignDto): MenuProps["items"] => {
  const items: MenuProps["items"] = [{ key: "edit", label: "Edit" }];

  if (record.codePrefix) {
    items.push({ key: "cpa", label: "CPA" });
  }

  items.push(
    { key: "promo", label: "Promo codes" },
    { type: "divider" },
    { key: "delete", label: "Delete", danger: true },
  );

  return items;
};

const defaultCampaign: CreateCampaignDto = {
  name: "",
  data: { tokens: 0 },
  type: CampaignType.TOKENS,
  status: CampaignStatus.ACTIVE,
  codePrefix: "",
};

const Campaigns = () => {
  const navigate = useNavigate();
  const campaignsService = appInject<ICampaignsService>(
    DI_TOKENS.campaignsService,
  );
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [cpaModalOpen, setCpaModalOpen] = useState(false);
  const [editing, setEditing] = useState<CampaignDto | null>(null);
  const [cpaCampaign, setCpaCampaign] = useState<CampaignDto | null>(null);
  const [form] = Form.useForm<CreateCampaignDto>();
  const [cpaForm] = Form.useForm<{ cpaUsd: number; effectiveFrom: dayjs.Dayjs }>();
  const [page, setPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(20);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const campaignsQuery = useQuery(
    ["campaigns", page, currentPageSize],
    () => campaignsService.list({ page, pageSize: currentPageSize }),
  );

  const campaigns = campaignsQuery.data?.campaigns ?? [];

  const cpaVersionsQuery = useQuery(
    ["campaign-cpa-versions", cpaCampaign?.id],
    () => campaignsService.listCpaVersions(cpaCampaign!.id),
    { enabled: Boolean(cpaCampaign?.id) },
  );
  const cpaVersions = cpaVersionsQuery.data ?? [];

  const saveMutation = useMutation(
    async (values: CreateCampaignDto) => {
      const codePrefix = values.codePrefix?.trim().toUpperCase() ?? "";
      const payload = { ...values, codePrefix };
      if (editing) {
        return campaignsService.update(editing.id, payload);
      }
      return campaignsService.create(payload);
    },
    {
      onSuccess: () => {
        void message.success(editing ? "Campaign updated" : "Campaign created");
        setModalOpen(false);
        setEditing(null);
        queryClient.invalidateQueries(["campaigns"]);
        setPage(1);
      },
      onError: () => {
        void message.error("Failed to save campaign");
      },
    },
  );

  const deleteMutation = useMutation(
    (id: string) => campaignsService.delete(id),
    {
      onSuccess: () => {
        void message.success("Campaign deleted");
        queryClient.invalidateQueries(["campaigns"]);
        setPage(1);
      },
      onError: () => {
        void message.error("Failed to delete campaign");
      },
    },
  );

  const addCpaMutation = useMutation(
    async (values: { cpaUsd: number; effectiveFrom: dayjs.Dayjs }) => {
      if (!cpaCampaign) return;
      return campaignsService.addCpaVersion(cpaCampaign.id, {
        cpaUsd: values.cpaUsd,
        effectiveFrom: values.effectiveFrom.toISOString(),
      });
    },
    {
      onSuccess: () => {
        void message.success("CPA version added");
        cpaForm.resetFields();
        queryClient.invalidateQueries(["campaign-cpa-versions", cpaCampaign?.id]);
      },
      onError: () => {
        void message.error("Failed to add CPA version");
      },
    },
  );

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue(defaultCampaign);
    setModalOpen(true);
  };

  const openEdit = (campaign: CampaignDto) => {
    setEditing(campaign);
    form.setFieldsValue({
      name: campaign.name,
      data: campaign.data,
      type: campaign.type,
      status: campaign.status,
      codePrefix: campaign.codePrefix ?? "",
    });
    setModalOpen(true);
  };

  const handleCampaignAction = useCallback(
    (key: string, record: CampaignDto) => {
      switch (key) {
        case "edit":
          openEdit(record);
          break;
        case "cpa":
          setCpaCampaign(record);
          setCpaModalOpen(true);
          break;
        case "promo":
          navigate(`${ROUTES.CAMPAIGN_PROMO_CODES}?campaignId=${record.id}`);
          break;
        case "delete":
          deleteMutation.mutate(record.id);
          break;
        default:
          break;
      }
    },
    [deleteMutation, navigate],
  );

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [campaigns.length, campaignsQuery.data?.total]);

  useEffect(() => {
    if (!cpaModalOpen) return;
    cpaForm.resetFields();
    cpaForm.setFieldsValue({ effectiveFrom: dayjs() });
  }, [cpaModalOpen, cpaCampaign?.id, cpaForm]);

  const closeCpaModal = () => {
    setCpaModalOpen(false);
    setCpaCampaign(null);
    cpaForm.resetFields();
  };

  const columns: ColumnsType<CampaignDto> = useMemo(
    () => [
      {
        title: "Actions",
        key: "actions",
        width: 130,
        render: (_, record) => (
          <Dropdown
            menu={{
              items: getCampaignActionItems(record),
              onClick: ({ key }) => handleCampaignAction(key, record),
            }}
          >
            <div style={{ color: "#1677ff", cursor: "pointer" }}>
              Actions <DownOutlined />
            </div>
          </Dropdown>
        ),
      },
      { title: "Name", dataIndex: "name", key: "name" },
      {
        title: "Prefix",
        dataIndex: "codePrefix",
        key: "codePrefix",
        render: (v: string | null) =>
          v ? <Tag color="blue">{v}</Tag> : <Tag>Legacy</Tag>,
      },
      {
        title: "Tokens",
        key: "tokens",
        render: (_, r) => r.data.tokens,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (s: CampaignStatus) =>
          s === CampaignStatus.ACTIVE ? (
            <Tag color="#009E60">Active</Tag>
          ) : (
            <Tag color="#f5222d">Inactive</Tag>
          ),
      },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 200,
        render: (date: string) => formatTableDateTime(date),
      },
    ],
    [handleCampaignAction],
  );

  const cpaColumns: ColumnsType<CampaignCpaVersionDto> = [
    {
      title: "CPA (USD)",
      dataIndex: "cpaUsd",
      key: "cpaUsd",
      render: (v: string) => `$${Number(v).toFixed(2)}`,
    },
    {
      title: "Effective from",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      render: (v: string) => formatTableDateTime(v),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (v: string) => formatTableDateTime(v),
    },
  ];

  return (
    <Layout className={styles.tableLayout}>
      <Row
        style={{
          height: 32,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Col
          span={7}
          style={{
            display: "flex",
            alignItems: "center",
            rowGap: 5,
            columnGap: 5,
          }}
        >
          <Typography.Title style={{ margin: 0 }} level={5}>
            Campaigns ({campaignsQuery.data?.total ?? 0})
          </Typography.Title>
          <Button type="primary" onClick={openCreate}>
            New campaign
          </Button>
        </Col>
      </Row>

      <Layout.Content
        ref={containerRef}
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Table
          rowKey="id"
          loading={campaignsQuery.isFetching}
          columns={columns}
          dataSource={campaigns}
          onChange={onTableChange}
          pagination={{
            current: page,
            total: campaignsQuery.data?.total ?? 0,
            pageSize: currentPageSize,
            showSizeChanger: true,
          }}
          size="small"
          scroll={{ y: containerHeight }}
        />
      </Layout.Content>

      <Modal
        title={editing ? "Edit campaign" : "New campaign"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => saveMutation.mutate(v)}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="codePrefix"
            label="Code prefix (affiliate)"
            extra={
              editing?.codePrefix
                ? "Required. Cannot be removed once set. Example: NVIG"
                : editing
                  ? "Optional for legacy campaigns. Example: NVIG"
                  : "Required for new affiliate campaigns. Example: NVIG"
            }
            rules={
              editing?.codePrefix
                ? [
                    { required: true, message: "Code prefix is required" },
                    {
                      pattern: /^[A-Z0-9]+$/,
                      message: "Use uppercase letters and numbers only",
                    },
                  ]
                : editing
                  ? [
                      {
                        pattern: /^[A-Z0-9]*$/,
                        message: "Use uppercase letters and numbers only",
                      },
                    ]
                  : [
                      { required: true, message: "Code prefix is required" },
                      {
                        pattern: /^[A-Z0-9]+$/,
                        message: "Use uppercase letters and numbers only",
                      },
                    ]
            }
          >
            <Input placeholder="NVIG" style={{ textTransform: "uppercase" }} />
          </Form.Item>
          <Form.Item
            name={["data", "tokens"]}
            label="Tokens to grant"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { label: "Active", value: CampaignStatus.ACTIVE },
                { label: "Inactive", value: CampaignStatus.INACTIVE },
              ]}
            />
          </Form.Item>
          <Form.Item name="type" hidden initialValue={CampaignType.TOKENS}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Affiliate payout (CPA)"
        open={cpaModalOpen}
        onCancel={closeCpaModal}
        footer={null}
        width={640}
        destroyOnClose
      >
        {cpaCampaign ? (
          <Typography.Paragraph type="secondary" className={styles.cpaModalIntro}>
            <Typography.Text strong>{cpaCampaign.name}</Typography.Text>
            {cpaCampaign.codePrefix ? (
              <>
                {" "}
                · prefix{" "}
                <Tag color="blue" style={{ marginInline: 0 }}>
                  {cpaCampaign.codePrefix}
                </Tag>
              </>
            ) : null}
            <br />
            Payout per qualifying promo redemption. The active rate at redemption
            time is stored on each code.
          </Typography.Paragraph>
        ) : null}

        <div className={styles.cpaFormPanel}>
          <Typography.Title level={5} className={styles.cpaSectionTitle}>
            Add payout version
          </Typography.Title>
          <Form
            form={cpaForm}
            layout="vertical"
            onFinish={(v) => addCpaMutation.mutate(v)}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="cpaUsd"
                  label="Payout amount (USD)"
                  rules={[
                    { required: true, message: "Enter a payout amount" },
                    {
                      type: "number",
                      min: 0,
                      message: "Amount must be zero or greater",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    addonBefore="$"
                    placeholder="100.00"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="effectiveFrom"
                  label="Effective from"
                  rules={[{ required: true, message: "Select effective date" }]}
                  extra="Redemptions on or after this time use this rate."
                >
                  <DatePicker showTime style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Space className={styles.cpaFormActions}>
              <Button onClick={closeCpaModal}>Close</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={addCpaMutation.isLoading}
              >
                Add version
              </Button>
            </Space>
          </Form>
        </div>

        <Divider className={styles.cpaDivider} />

        <Typography.Title level={5} className={styles.cpaSectionTitle}>
          Version history
        </Typography.Title>
        <Table
          rowKey="id"
          size="small"
          columns={cpaColumns}
          dataSource={cpaVersions}
          loading={cpaVersionsQuery.isFetching}
          pagination={false}
          locale={{
            emptyText: "No payout versions yet. Add the first rate above.",
          }}
        />
      </Modal>
    </Layout>
  );
};

export default Campaigns;
